import { useMemo } from 'react';
import { useSalesforceData } from './useSalesforceData';
import { soqlDateFilter, TimePeriod } from '../utils/timePeriods';
import { DemoEvent, OpportunityRow } from '../types/gtm';
import { RECORD_TYPES } from '../constants';

export interface UseAEDashboardResult {
  demosRunCount: number;
  demosScheduledCount: number;
  demosRun: DemoEvent[];
  demosScheduled: DemoEvent[];
  opportunities: OpportunityRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export function useAEDashboard(
  aeId: string | null,
  period: TimePeriod,
  source: string | null
): UseAEDashboardResult {
  // Query 1 — Demo Events (drives the Demos Run / Demos Scheduled KPIs)
  const demoSoql = `SELECT Id, Subject, ActivityDate, WhoId, WhatId, OwnerId, Calendly__InviteeUuid__c FROM Event WHERE Subject LIKE '%Demo%' AND Calendly__InviteeUuid__c != null AND (NOT Subject LIKE '%Canceled%') AND ${soqlDateFilter('ActivityDate', period)}`;

  const {
    data: demoEvents,
    loading: demosLoading,
    error: demosError,
    refetch: refetchDemos,
  } = useSalesforceData<DemoEvent>(demoSoql);

  // Client-side filtering
  const demosRun = useMemo(
    () => {
      const today = new Date().toISOString().slice(0, 10);
      return demoEvents.filter(
        (e) =>
          e.ActivityDate <= today &&
          (!aeId || e.OwnerId === aeId)
      );
    },
    [demoEvents, aeId]
  );

  const demosScheduled = useMemo(
    () => demoEvents.filter((e) => (!aeId || e.OwnerId === aeId)),
    [demoEvents, aeId]
  );

  // Query 2 — Sales Opportunities queried directly by Owner + record type.
  // (Previously derived from demo WhatIds, which silently dropped any opp
  // without a linked demo Event.) Returns all OPEN opps — the current
  // pipeline, period-independent — plus opps CLOSED within the selected period.
  const oppSoql = useMemo(() => {
    const ownerFilter = aeId ? ` AND OwnerId = '${aeId}'` : '';
    const sourceFilter = source ? ` AND LeadSource = '${source}'` : '';
    // Sales opps stay on Account (their Encore_Firm__c lookup is empty), but
    // Number_of_Households__c moved from Account onto the Opportunity itself.
    return `SELECT Id, Name, StageName, Amount, CreatedDate, CloseDate, NextStep, OwnerId, LeadSource, Won_Reason__c, Loss_Reason__c, LastActivityDate, Number_of_Households__c, Account.Name, Account.IMO_Affiliations__c, Account.Competition_Currently_Using__c, Account.Competition_Prev_Using__c FROM Opportunity WHERE RecordTypeId = '${RECORD_TYPES.SALES_OPPORTUNITY}'${ownerFilter}${sourceFilter} AND (IsClosed = false OR (${soqlDateFilter('CloseDate', period)}))`;
  }, [aeId, source, period]);

  const {
    data: opportunities,
    loading: oppsLoading,
    error: oppsError,
    refetch: refetchOpps,
  } = useSalesforceData<OpportunityRow>(oppSoql);

  const loading = demosLoading || oppsLoading;
  const error = demosError || oppsError || null;

  const refetch = () => {
    refetchDemos();
    refetchOpps();
  };

  return {
    demosRunCount: demosRun.length,
    demosScheduledCount: demosScheduled.length,
    demosRun,
    demosScheduled,
    opportunities,
    loading,
    error,
    refetch,
  };
}
